import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useCreateCampaign, useUpdateCampaign } from '@/hooks/useCampaigns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCampaignTemplates } from '@/hooks/useCampaignTemplates';
import { Campaign } from '@/services/campaignService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useDefaultSender } from '@/hooks/useDefaultSender';
import { Mail, Users, Upload, Loader2 } from 'lucide-react';
import MergeFieldPicker from './MergeFieldPicker';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { importRecipientsFromCSV } from '@/services/campaignRecipientImportService';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  campaign?: Campaign | null;
}

const CampaignCreateDialog: React.FC<Props> = ({ open, onOpenChange, companyId, campaign }) => {
  const isEdit = !!campaign;
  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();
  const { data: templates = [] } = useCampaignTemplates(companyId);
  const { profile } = useAuth();
  const { defaultSender } = useDefaultSender();
  const queryClient = useQueryClient();
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [companyEmail, setCompanyEmail] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [textContent, setTextContent] = useState('');
  const [senderSource, setSenderSource] = useState('system_sendgrid');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [customRecipients, setCustomRecipients] = useState<Array<{ email: string; name: string }>>([]);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientName, setNewRecipientName] = useState('');
  const [templateId, setTemplateId] = useState<string>('');

  const { data: queuedRecipients = [], isLoading: recipientsLoading } = useQuery({
    queryKey: ['campaign-recipients-inline', campaign?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_queue')
        .select('id, recipient_name, recipient_email, subject, status')
        .eq('campaign_id', campaign!.id)
        .order('recipient_name');
      if (error) throw error;
      return data || [];
    },
    enabled: isEdit && open && !!campaign?.id,
  });

  useEffect(() => {
    if (!companyId) return;
    supabase.from('companies').select('default_from_email').eq('id', companyId).single()
      .then(({ data }) => setCompanyEmail(data?.default_from_email || null));
  }, [companyId]);

  const resolvedEmail = senderSource === 'system_sendgrid'
    ? companyEmail
    : senderSource === 'user_sendgrid'
      ? defaultSender?.from_email
      : fromEmail || null;

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setDescription(campaign.description || '');
      setSubject(campaign.subject);
      setHtmlContent(campaign.html_content);
      setTextContent(campaign.text_content || '');
      setSenderSource(campaign.sender_config?.source || 'system_sendgrid');
      setFromEmail(campaign.sender_config?.from_email || '');
      setFromName(campaign.sender_config?.from_name || '');
      setTargetType(campaign.recipient_filters?.target || 'all');
      setSelectedStatuses(campaign.recipient_filters?.statuses || []);
      setSelectedCategories(campaign.recipient_filters?.categories || []);
      setCustomRecipients(campaign.recipient_filters?.recipients || []);
      setTemplateId(campaign.template_id || 'none');
    } else {
      setName(''); setDescription(''); setSubject(''); setHtmlContent(''); setTextContent('');
      setSenderSource('system_sendgrid'); setFromEmail(''); setFromName('');
      setTargetType('all'); setSelectedStatuses([]); setSelectedCategories([]); setCustomRecipients([]);
      setTemplateId('none');
    }
  }, [campaign, open]);

  const handleTemplateSelect = (tId: string) => {
    if (tId === 'none') {
      setTemplateId('none');
      return;
    }
    setTemplateId(tId);
    const tpl = templates.find(t => t.id === tId);
    if (tpl) {
      setSubject(tpl.subject_template);
      setHtmlContent(tpl.body_template);
    }
  };

  const handleSave = async () => {
    const payload = {
      company_id: companyId,
      name,
      description: description || null,
      subject,
      html_content: htmlContent,
      text_content: textContent || null,
      template_id: templateId === 'none' ? null : templateId || null,
      sender_config: { source: senderSource, from_email: fromEmail, from_name: fromName },
      recipient_filters: targetType === 'from_queue'
        ? { target: 'from_queue' }
        : {
            target: targetType,
            ...(targetType === 'by_status' ? { statuses: selectedStatuses } : {}),
            ...(targetType === 'by_category' ? { categories: selectedCategories } : {}),
            ...(targetType === 'custom' ? { recipients: customRecipients } : {}),
          },
      created_by: (await supabase.auth.getUser()).data.user?.id,
    };

    if (isEdit && campaign) {
      await updateMutation.mutateAsync({ id: campaign.id, updates: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Campaign' : 'Create Campaign'}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details" className="mt-2">
          <TabsList className="w-full">
            <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
            <TabsTrigger value="content" className="flex-1">Content</TabsTrigger>
            <TabsTrigger value="recipients" className="flex-1">Recipients</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Campaign Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. February Outreach" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description..." rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sender Config</Label>
                <Select value={senderSource} onValueChange={setSenderSource}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system_sendgrid">
                      <div className="flex flex-col items-start">
                        <span>Company Default</span>
                        <span className="text-xs text-muted-foreground">{companyEmail ? `Uses ${companyEmail}` : 'Company sending address'}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="user_sendgrid">
                      <div className="flex flex-col items-start">
                        <span>My Verified Email</span>
                        <span className="text-xs text-muted-foreground">{defaultSender?.from_email ? `Uses ${defaultSender.from_email}` : 'Your personal verified sender'}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="manual">
                      <div className="flex flex-col items-start">
                        <span>Custom Sender</span>
                        <span className="text-xs text-muted-foreground">Manually specify a from address</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {resolvedEmail && senderSource !== 'manual' && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Emails will be sent from: <span className="font-medium">{resolvedEmail}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Template</Label>
                <Select value={templateId} onValueChange={handleTemplateSelect}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {senderSource === 'manual' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Email</Label>
                  <Input value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="sender@company.com" />
                </div>
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input value={fromName} onChange={e => setFromName(e.target.value)} placeholder="John Doe" />
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="content" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Subject Line *</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. {{lead_name}}, we'd love to connect" />
              <p className="text-xs text-muted-foreground">Supports merge fields like {'{{lead_name}}'}</p>
            </div>
            <div className="space-y-2">
              <Label>HTML Content *</Label>
              <RichTextEditor value={htmlContent} onChange={setHtmlContent} placeholder="Write your email content..." />
            </div>
            <MergeFieldPicker onInsert={(field) => setHtmlContent(prev => prev + `{{${field}}}`)} />
            <div className="space-y-2">
              <Label>Plain Text (optional)</Label>
              <Textarea value={textContent} onChange={e => setTextContent(e.target.value)} rows={3} placeholder="Plain text version..." />
            </div>
          </TabsContent>

          <TabsContent value="recipients" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Select value={targetType} onValueChange={setTargetType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leads</SelectItem>
                  <SelectItem value="by_status">By Status</SelectItem>
                  <SelectItem value="by_category">By Category</SelectItem>
                  <SelectItem value="custom">Custom Recipients</SelectItem>
                  <SelectItem value="from_queue">From Recipient List</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {targetType === 'from_queue' && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  This campaign uses a pre-built recipient list. Use "View Recipients" to manage it.
                </p>
                {isEdit && campaign?.id ? (
                  <>
                    <input
                      type="file"
                      ref={csvInputRef}
                      accept=".csv,.tsv,.txt"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !campaign?.id) return;
                        setIsImporting(true);
                        try {
                          const text = await file.text();
                          const result = await importRecipientsFromCSV(campaign.id, text, companyId);
                          queryClient.invalidateQueries({ queryKey: ['campaign-recipients-inline', campaign.id] });
                          queryClient.invalidateQueries({ queryKey: ['campaign-recipients'] });
                          queryClient.invalidateQueries({ queryKey: ['campaigns'] });
                          toast.success(`Imported ${result.imported} recipients (${result.matched} matched to leads)`, {
                            description: `${result.duplicates} duplicates skipped${result.unmatchedCount > 0 ? `. ${result.unmatchedCount} not matched to leads.` : ''}`,
                          });
                        } catch (err: any) {
                          toast.error('Import failed', { description: err.message });
                        } finally {
                          setIsImporting(false);
                          if (csvInputRef.current) csvInputRef.current.value = '';
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => csvInputRef.current?.click()}
                      disabled={isImporting}
                    >
                      {isImporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {isImporting ? 'Importing...' : 'Import CSV'}
                    </Button>
                  </>
                ) : !isEdit ? (
                  <p className="text-xs text-muted-foreground italic">Save the campaign first to import recipients via CSV.</p>
                ) : null}
              </div>
            )}
            {targetType === 'by_status' && (
              <div className="space-y-2">
                <Label>Statuses</Label>
                <div className="flex flex-wrap gap-2">
                  {['new', 'contacted', 'qualified', 'negotiation', 'won', 'lost'].map(s => (
                    <Button
                      key={s}
                      size="sm"
                      variant={selectedStatuses.includes(s) ? 'default' : 'outline'}
                      onClick={() => setSelectedStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {targetType === 'by_category' && (
              <div className="space-y-2">
                <Label>Categories (comma-separated)</Label>
                <Input
                  value={selectedCategories.join(', ')}
                  onChange={e => setSelectedCategories(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder="Restaurant, Hotel, ..."
                />
              </div>
            )}
            {targetType === 'custom' && (
              <div className="space-y-3">
                <Label>Add Recipients</Label>
                <div className="flex gap-2">
                  <Input
                    value={newRecipientEmail}
                    onChange={e => setNewRecipientEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="flex-1"
                    type="email"
                  />
                  <Input
                    value={newRecipientName}
                    onChange={e => setNewRecipientName(e.target.value)}
                    placeholder="Name (optional)"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (newRecipientEmail.trim()) {
                        setCustomRecipients(prev => [...prev, { email: newRecipientEmail.trim(), name: newRecipientName.trim() }]);
                        setNewRecipientEmail('');
                        setNewRecipientName('');
                      }
                    }}
                    disabled={!newRecipientEmail.trim()}
                  >
                    Add
                  </Button>
                </div>
                {customRecipients.length > 0 && (
                  <ScrollArea className="h-[160px] border rounded-md">
                    <div className="divide-y">
                      {customRecipients.map((r, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div className="min-w-0 flex-1">
                            {r.name && <span className="font-medium mr-2">{r.name}</span>}
                            <span className="text-muted-foreground">{r.email}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={() => setCustomRecipients(prev => prev.filter((_, idx) => idx !== i))}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                <p className="text-xs text-muted-foreground">
                  {customRecipients.length} recipient{customRecipients.length !== 1 ? 's' : ''} added. These will be queued when the campaign is sent.
                </p>
              </div>
            )}

            {isEdit && (
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <Label>Queued Recipients</Label>
                  {queuedRecipients.length > 0 && (
                    <Badge variant="secondary" className="text-xs">{queuedRecipients.length}</Badge>
                  )}
                </div>
                {recipientsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading recipients...</p>
                ) : queuedRecipients.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No recipients queued yet</p>
                ) : (
                  <ScrollArea className="h-[200px] border rounded-md">
                    <div className="divide-y">
                      {queuedRecipients.map(r => (
                        <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div className="min-w-0 flex-1">
                            <span className="font-medium">{r.recipient_name || 'Unknown'}</span>
                            <span className="text-muted-foreground ml-2 truncate">{r.recipient_email}</span>
                          </div>
                          <Badge variant={r.status === 'sent' ? 'default' : r.status === 'failed' ? 'destructive' : 'outline'} className="text-xs ml-2 shrink-0">
                            {r.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                <p className="text-xs text-muted-foreground">Use "View Recipients" from the campaign menu for full editing capabilities.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!name || !subject || !htmlContent || createMutation.isPending || updateMutation.isPending}>
            {isEdit ? 'Save Changes' : 'Create Campaign'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignCreateDialog;
